import { Link } from "wouter";
import { useGetLeadsSummary, useGetOperationsAlerts } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, AlertCircle, PhoneCall, TrendingUp } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { ChartContainer, ChartTooltipContent } from "@/components/ui/chart";
import { ALERT_TIER_COLORS } from "@/lib/alert-tier";

function InventoryAlertsWidget() {
  const { data: alerts = [] } = useGetOperationsAlerts({});
  const counts = alerts.reduce<Record<string, number>>((acc, a) => {
    acc[a.tier] = (acc[a.tier] ?? 0) + 1;
    return acc;
  }, {});
  const total = (counts["Black"] ?? 0) + (counts["Red"] ?? 0) + (counts["Green"] ?? 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Link href="/operations" className="hover:underline">Inventory Alerts</Link>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {total === 0 ? (
          <div className="text-sm text-muted-foreground">Stock looks healthy.</div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {(["Black", "Red", "Green"] as const).map((t) =>
              (counts[t] ?? 0) > 0 ? (
                <span key={t} className={`px-2 py-1 rounded text-xs font-medium ${ALERT_TIER_COLORS[t]}`}>
                  {counts[t]} {t}
                </span>
              ) : null
            )}
            <Link href="/operations" className="ml-auto text-xs underline text-muted-foreground">View all →</Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  // All-time summary; period-scoped funnel now lives on the Leads page.
  const { data: summary, isLoading } = useGetLeadsSummary();

  if (isLoading || !summary) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}><CardContent className="p-6"><Skeleton className="h-16 w-full" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Open Leads</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.openLeads}</div>
            <p className="text-xs text-muted-foreground">Out of {summary.total} total leads</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Follow-ups Due Today</CardTitle>
            <AlertCircle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.followUpsDueToday}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Callbacks Due Today</CardTitle>
            <PhoneCall className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.callbacksDueToday}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary.total > 0 ? Math.round((summary.wonLeads / summary.total) * 100) : 0}%
            </div>
            <p className="text-xs text-muted-foreground">{summary.wonLeads} won, {summary.lostLeads} lost</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <InventoryAlertsWidget />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Leads by Stage (All-time)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ChartContainer config={{ count: { label: "Leads", color: "hsl(var(--primary))" } }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={summary.byStage}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="stage" tickFormatter={(v) => v.replace(/_/g, " ")} fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis fontSize={12} tickLine={false} axisLine={false} />
                    <RechartsTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="count" fill="var(--color-count)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Leads by Source (All-time)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ChartContainer config={{ count: { label: "Leads", color: "hsl(var(--chart-2))" } }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={summary.bySource} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis dataKey="source" type="category" width={100} tickFormatter={(v) => v.replace(/_/g, " ")} fontSize={12} tickLine={false} axisLine={false} />
                    <RechartsTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="count" fill="var(--color-count)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
